import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileDown, FileUp, Search, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exporters";
import Pagination from "@/components/Pagination";
import { useResponsivePageSize } from "@/hooks/useResponsivePageSize";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";

const EMPTY = { name: "", company: "", poc_name: "", poc_email: "", poc_phone: "", address: "",
  tax_id: "", payment_terms: "Net 30", product_categories: "", website: "", account_number: "", notes: "" };

export default function VendorsPage() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const PAGE_SIZE = useResponsivePageSize(10, 25);

  const load = async () => setVendors(await api.get("/vendors").then(r => r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name || "", company: v.company || "", poc_name: v.poc_name || "",
      poc_email: v.poc_email || "", poc_phone: v.poc_phone || "", address: v.address || "",
      tax_id: v.tax_id || "", payment_terms: v.payment_terms || "Net 30",
      product_categories: v.product_categories || "", website: v.website || "",
      account_number: v.account_number || "", notes: v.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/vendors/${editing.id}`, form); toast.success("Vendor updated"); }
      else { await api.post("/vendors", form); toast.success("Vendor added"); }
      setForm(EMPTY); setEditing(null); setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Vendor deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/vendors/import.csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} vendors`); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const filtered = useMemo(
    () => vendors.filter(v => !q || [v.name, v.company, v.poc_name, v.product_categories].some(s => s?.toLowerCase().includes(q.toLowerCase()))),
    [vendors, q]
  );
  useEffect(() => { setPage(0); }, [q]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <p className="label-eyebrow">Beauty supply network</p>
          <h1 className="text-4xl font-bold tracking-tight">Vendors</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <Input className="pl-8 w-64" placeholder="Search vendors…" value={q} onChange={e => setQ(e.target.value)} data-testid="vendors-search" />
          </div>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => downloadCsv("/vendors/export.csv", "vendors.csv")} data-testid="export-vendors-csv"><FileDown size={14} className="mr-1" /> Export</Button>
              <label className="inline-flex">
                <input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-vendors-csv" />
                <span className="inline-flex items-center cursor-pointer px-4 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)]"><FileUp size={14} className="mr-1" /> Import CSV</span>
              </label>
              <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
                <DialogTrigger asChild><Button className="btn-brand" onClick={openNew} data-testid="add-vendor-button"><Plus size={14} className="mr-1" /> Vendor</Button></DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle>{editing ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
                  <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="vendor-form">
                    <div><Label>Name *</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
                    <div><Label>Company</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} data-testid="vendor-company" /></div>
                    <div><Label>POC Name</Label><Input value={form.poc_name} onChange={e => setForm({ ...form, poc_name: e.target.value })} data-testid="vendor-poc" /></div>
                    <div><Label>POC Email</Label><Input type="email" value={form.poc_email} onChange={e => setForm({ ...form, poc_email: e.target.value })} /></div>
                    <div><Label>POC Phone</Label><Input value={form.poc_phone} onChange={e => setForm({ ...form, poc_phone: e.target.value })} /></div>
                    <div><Label>Tax ID / EIN</Label><Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} /></div>
                    <div><Label>Payment terms</Label><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} /></div>
                    <div><Label>Account #</Label><Input value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Product categories</Label><Input value={form.product_categories} onChange={e => setForm({ ...form, product_categories: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Website</Label><Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                    <Button className="btn-brand col-span-2" data-testid="vendor-submit">{editing ? "Save changes" : "Add vendor"}</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </header>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3 w-10"><SelectAllCheckbox records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /></th>
                <th className="text-left p-3 sticky left-0 bg-[var(--bg-soft)] z-20 min-w-[90px]">Name</th>
                <th className="text-left p-3 sticky left-[90px] bg-[var(--bg-soft)] z-20 min-w-[120px] border-r border-[var(--border)]">Company</th>
                <th className="text-left p-3 min-w-[110px]">POC</th>
                <th className="text-left p-3 hidden md:table-cell">Categories</th>
                <th className="text-left p-3">Terms</th>
                <th className="text-left p-3 hidden md:table-cell">Tax ID</th>
                {isAdmin && <th className="text-right p-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paged.map(v => (
                <tr key={v.id} className="table-row border-t border-[var(--border)] group" data-testid={`vendor-row-${v.id}`}>
                  <td className="p-3"><SelectionCheckbox checked={selectedIds.includes(v.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, v.id] : selectedIds.filter((id) => id !== v.id))} testId={`select-row-checkbox-${v.id}`} label={`Select ${v.name}`} /></td>
                  <td className="p-3 font-medium capitalize sticky left-0 bg-white group-hover:bg-[var(--bg-soft)] z-10 min-w-[90px]">{v.name}</td>
                  <td className="p-3 capitalize sticky left-[90px] bg-white group-hover:bg-[var(--bg-soft)] z-10 min-w-[120px] border-r border-[var(--border)]">{v.company || "—"}</td>
                  <td className="p-3 capitalize min-w-[110px]">{v.poc_name || "—"}{v.poc_email && <div className="text-xs text-gray-500 normal-case">{v.poc_email}</div>}</td>
                  <td className="p-3 text-gray-600 max-w-xs truncate hidden md:table-cell">{v.product_categories || "—"}</td>
                  <td className="p-3">{v.payment_terms}</td>
                  <td className="p-3 text-gray-500 hidden md:table-cell">{v.tax_id || "—"}</td>
                  {isAdmin && (
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(v)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-vendor-${v.id}`}><Trash2 size={14} /></Button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center text-gray-400">No vendors yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={filtered.length} pageSize={PAGE_SIZE} testid="vendors-pagination" />
      </Card>
      <BulkActions section="vendors" records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />
    </div>
  );
}

